import img1 from "@/assets/fragrance-1.jpg";
import img2 from "@/assets/fragrance-2.jpg";
import img3 from "@/assets/fragrance-3.jpg";
import img4 from "@/assets/fragrance-4.jpg";

export type FragranceFamily =
  | "Floral"
  | "Oriental"
  | "Woody"
  | "Leather"
  | "Chypre"
  | "Amber"
  | "Green";

export type Gender = "Feminine" | "Masculine" | "Unisex";

export type Concentration = "EDP" | "EDT" | "Parfum" | "Extrait";

/** One editable benefit statement shown under Add to Cart on the PDP. */
export type ProductBenefit = { text: string; enabled: boolean };

/**
 * Default PDP benefit statements. Products without saved benefits render
 * these, preserving the storefront appearance that existed before the
 * Admin editor controls were added.
 */
export const DEFAULT_PRODUCT_BENEFITS: ProductBenefit[] = [
  { text: "Hand-blended in Mumbai, India", enabled: true },
  { text: "Numbered flacon, refill programme", enabled: true },
  { text: "Free Standard Delivery on qualifying orders", enabled: true },
  { text: "30-day returns on unopened flacons", enabled: true },
];

export type CatalogProduct = {
  id: string;
  slug: string;
  name: string;
  family: FragranceFamily;
  gender: Gender;
  concentration: Concentration;
  notes: string;
  keyNotes: string[];
  /**
   * "Starting from" price (mirrors the 50 ML price). Kept for sorting,
   * filtering and card display — actual selling prices live in `prices`.
   */
  price: number;
  /** Per-bottle-size selling prices (10 / 20 / 50 / 100 ML), in rupees. */
  prices?: import("./size-pricing").SizePrices;
  img: string;
  badge?: "New" | "Bestseller" | "Limited";
  collectionId: CollectionId;
  createdAt: string; // ISO
  /**
   * Category slug (matches CategoryRegistry). Optional for backward-compat:
   * absent values are treated as "personal-perfumes" (the RC1 catalog).
   */
  categoryId?: string;
  /** Optional stock keeping unit — added in Phase 2 seed data. */
  sku?: string;
  /** Optional short marketing description used on cards / SEO. */
  shortDescription?: string;
  /** Optional long-form description used on the PDP. */
  longDescription?: string;
  /** Optional size/volume label, e.g. "50 ml", "100 ml refill". */
  size?: string;
  /** Optional structured note breakdown (Phase 2). */
  topNotes?: string[];
  heartNotes?: string[];
  baseNotes?: string[];
  /** Product lifecycle status. */
  status?: "active" | "coming-soon" | "archived";
  /**
   * Channels this product may be sold through. Defaults to ["individual"],
   * which is the storefront catalog behaviour that has always applied.
   */
  salesChannels?: string[];
  /** Optional SEO meta title override; falls back to product name. */
  metaTitle?: string;
  /** Optional SEO meta description override; falls back to shortDescription. */
  metaDescription?: string;
  /** Optional additional image gallery URLs (Media Library asset URLs). */
  gallery?: string[];
  /** Homepage visibility: show this product in the Best Sellers rail. */
  showInBestSellers?: boolean;
  /** Homepage visibility: show this product in the New Arrivals rail. */
  showInNewArrivals?: boolean;
  /** Discount percentage (0-100). If set > 0, shows discounted price to customers. */
  discountPercent?: number;
  /**
   * Editable PDP benefit statements (Admin → Products). Absent means the
   * storefront renders DEFAULT_PRODUCT_BENEFITS.
   */
  benefits?: ProductBenefit[];
  /**
   * Category-specific attributes (Phase 2 · AP-10 Plugin Contract).
   * Keys are declared by each CategoryPlugin's `attributeSchema`. May be
   * unset on RC1 products; unset rows are treated as "no attribute values".
   */
  attributes?: Record<string, unknown>;
};

export type CollectionId = "signatures" | "absolus" | "voyages" | "nocturnes" | "editions";

export type Collection = {
  id: CollectionId;
  name: string;
  tagline: string;
  description: string;
  cover: string;
};

export const COLLECTIONS: Collection[] = [
  {
    id: "signatures",
    name: "Signatures",
    tagline: "The house classics",
    description:
      "The compositions that define the CB16 house — timeless, hand-blended, endlessly reissued.",
    cover: img1,
  },
  {
    id: "absolus",
    name: "Absolus",
    tagline: "Rare unisex compositions",
    description: "Small-batch extraits built around a single rare natural. Numbered and limited.",
    cover: img4,
  },
  {
    id: "voyages",
    name: "Voyages",
    tagline: "Scents from far places",
    description: "A perfumer's travelogue — sourced from single-estate growers around the world.",
    cover: img3,
  },
  {
    id: "nocturnes",
    name: "Nocturnes",
    tagline: "For the hours after dark",
    description: "Warm, resinous, unapologetic. Compositions for evenings and beyond.",
    cover: img2,
  },
  {
    id: "editions",
    name: "Editions Limitées",
    tagline: "Vintage releases",
    description: "Discontinued and archival flacons, released once a year in tiny batches.",
    cover: img4,
  },
];

const IMAGES = [img1, img2, img3, img4];

function make(
  i: number,
  data: Omit<CatalogProduct, "id" | "slug" | "img"> & { imgIdx?: number },
): CatalogProduct {
  const slug = data.name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
  return {
    id: `cb16-${String(i).padStart(3, "0")}`,
    slug,
    img: IMAGES[(data.imgIdx ?? i) % IMAGES.length],
    ...data,
  };
}

export const PRODUCTS: CatalogProduct[] = [
  make(1, {
    name: "Ambre Noir",
    family: "Oriental",
    gender: "Unisex",
    concentration: "EDP",
    notes: "Amber, Vanilla, Oud",
    keyNotes: ["Amber", "Vanilla", "Oud"],
    price: 245,
    badge: "Bestseller",
    collectionId: "signatures",
    createdAt: "2024-02-01",
    imgIdx: 0,
  }),
  make(2, {
    name: "Rose de Mai",
    family: "Floral",
    gender: "Feminine",
    concentration: "EDP",
    notes: "Rose, Peony, Musk",
    keyNotes: ["Rose", "Peony", "Musk"],
    price: 220,
    collectionId: "signatures",
    createdAt: "2024-03-15",
    imgIdx: 1,
  }),
  make(3, {
    name: "Forêt Verte",
    family: "Woody",
    gender: "Masculine",
    concentration: "EDP",
    notes: "Vetiver, Cedar, Moss",
    keyNotes: ["Vetiver", "Cedar", "Moss"],
    price: 260,
    collectionId: "voyages",
    createdAt: "2024-05-10",
    imgIdx: 2,
  }),
  make(4, {
    name: "Cuir Onyx",
    family: "Leather",
    gender: "Masculine",
    concentration: "Parfum",
    notes: "Leather, Tobacco, Saffron",
    keyNotes: ["Leather", "Tobacco", "Saffron"],
    price: 290,
    badge: "Limited",
    collectionId: "absolus",
    createdAt: "2024-07-01",
    imgIdx: 3,
  }),
  make(5, {
    name: "Nuit Blanche",
    family: "Chypre",
    gender: "Unisex",
    concentration: "EDP",
    notes: "Bergamot, Iris, Patchouli",
    keyNotes: ["Bergamot", "Iris", "Patchouli"],
    price: 275,
    badge: "New",
    collectionId: "nocturnes",
    createdAt: "2025-11-01",
    imgIdx: 3,
  }),
  make(6, {
    name: "Sable d'Or",
    family: "Amber",
    gender: "Unisex",
    concentration: "Extrait",
    notes: "Amber, Saffron, Benzoin",
    keyNotes: ["Amber", "Saffron", "Benzoin"],
    price: 320,
    badge: "New",
    collectionId: "absolus",
    createdAt: "2025-12-05",
    imgIdx: 0,
  }),
  make(7, {
    name: "Pétales",
    family: "Floral",
    gender: "Feminine",
    concentration: "EDT",
    notes: "Jasmine, Tuberose, Sandalwood",
    keyNotes: ["Jasmine", "Tuberose", "Sandalwood"],
    price: 195,
    badge: "New",
    collectionId: "signatures",
    createdAt: "2025-10-10",
    imgIdx: 1,
  }),
  make(8, {
    name: "Émeraude",
    family: "Green",
    gender: "Unisex",
    concentration: "EDP",
    notes: "Fig, Galbanum, Vetiver",
    keyNotes: ["Fig", "Galbanum", "Vetiver"],
    price: 265,
    badge: "New",
    collectionId: "voyages",
    createdAt: "2025-09-20",
    imgIdx: 2,
  }),
  make(9, {
    name: "Oud Perdu",
    family: "Oriental",
    gender: "Masculine",
    concentration: "Extrait",
    notes: "Oud, Rose, Incense",
    keyNotes: ["Oud", "Rose", "Incense"],
    price: 380,
    badge: "Limited",
    collectionId: "absolus",
    createdAt: "2024-11-11",
    imgIdx: 3,
  }),
  make(10, {
    name: "Lumière",
    family: "Floral",
    gender: "Feminine",
    concentration: "EDP",
    notes: "Neroli, Orange Blossom, Musk",
    keyNotes: ["Neroli", "Orange Blossom", "Musk"],
    price: 210,
    collectionId: "signatures",
    createdAt: "2023-06-01",
    imgIdx: 1,
  }),
  make(11, {
    name: "Bois de Nuit",
    family: "Woody",
    gender: "Unisex",
    concentration: "EDP",
    notes: "Sandalwood, Cedar, Amber",
    keyNotes: ["Sandalwood", "Cedar", "Amber"],
    price: 240,
    collectionId: "nocturnes",
    createdAt: "2024-01-20",
    imgIdx: 2,
  }),
  make(12, {
    name: "Vétiver Cuivré",
    family: "Woody",
    gender: "Masculine",
    concentration: "EDT",
    notes: "Vetiver, Copper, Grapefruit",
    keyNotes: ["Vetiver", "Grapefruit", "Copper"],
    price: 180,
    collectionId: "voyages",
    createdAt: "2024-04-04",
    imgIdx: 2,
  }),
  make(13, {
    name: "Encens Sacré",
    family: "Oriental",
    gender: "Unisex",
    concentration: "Parfum",
    notes: "Incense, Myrrh, Amber",
    keyNotes: ["Incense", "Myrrh", "Amber"],
    price: 310,
    collectionId: "nocturnes",
    createdAt: "2024-08-15",
    imgIdx: 0,
  }),
  make(14, {
    name: "Iris Poudré",
    family: "Chypre",
    gender: "Feminine",
    concentration: "EDP",
    notes: "Iris, Violet, Cashmeran",
    keyNotes: ["Iris", "Violet", "Cashmeran"],
    price: 250,
    collectionId: "signatures",
    createdAt: "2024-06-22",
    imgIdx: 1,
  }),
  make(15, {
    name: "Ambre 1912",
    family: "Amber",
    gender: "Unisex",
    concentration: "Extrait",
    notes: "Amber, Labdanum, Vanilla",
    keyNotes: ["Amber", "Labdanum", "Vanilla"],
    price: 420,
    badge: "Limited",
    collectionId: "editions",
    createdAt: "2023-12-01",
    imgIdx: 3,
  }),
  make(16, {
    name: "Fleur Sauvage",
    family: "Green",
    gender: "Feminine",
    concentration: "EDT",
    notes: "Wildflower, Grass, Bergamot",
    keyNotes: ["Wildflower", "Grass", "Bergamot"],
    price: 165,
    collectionId: "voyages",
    createdAt: "2025-05-01",
    imgIdx: 1,
  }),
  make(17, {
    name: "Tabac Rare",
    family: "Leather",
    gender: "Masculine",
    concentration: "Parfum",
    notes: "Tobacco, Honey, Rum",
    keyNotes: ["Tobacco", "Honey", "Rum"],
    price: 295,
    collectionId: "nocturnes",
    createdAt: "2024-09-09",
    imgIdx: 3,
  }),
  make(18, {
    name: "Muguet Blanc",
    family: "Floral",
    gender: "Feminine",
    concentration: "EDT",
    notes: "Lily of the Valley, Green Leaves, Musk",
    keyNotes: ["Lily", "Musk", "Green Leaves"],
    price: 175,
    collectionId: "signatures",
    createdAt: "2023-04-15",
    imgIdx: 1,
  }),
  make(19, {
    name: "Santal Doré",
    family: "Woody",
    gender: "Unisex",
    concentration: "Extrait",
    notes: "Sandalwood, Gold Amber, Cardamom",
    keyNotes: ["Sandalwood", "Amber", "Cardamom"],
    price: 340,
    badge: "Bestseller",
    collectionId: "absolus",
    createdAt: "2024-10-30",
    imgIdx: 2,
  }),
  make(20, {
    name: "Épices d'Orient",
    family: "Oriental",
    gender: "Unisex",
    concentration: "EDP",
    notes: "Cardamom, Saffron, Rose",
    keyNotes: ["Cardamom", "Saffron", "Rose"],
    price: 255,
    collectionId: "voyages",
    createdAt: "2025-02-14",
    imgIdx: 0,
  }),
  make(21, {
    name: "Mousse Profonde",
    family: "Chypre",
    gender: "Masculine",
    concentration: "EDP",
    notes: "Oakmoss, Labdanum, Bergamot",
    keyNotes: ["Oakmoss", "Labdanum", "Bergamot"],
    price: 230,
    collectionId: "signatures",
    createdAt: "2024-03-30",
    imgIdx: 2,
  }),
  make(22, {
    name: "Néroli 44",
    family: "Floral",
    gender: "Unisex",
    concentration: "EDT",
    notes: "Neroli, Petitgrain, White Musk",
    keyNotes: ["Neroli", "Petitgrain", "Musk"],
    price: 190,
    badge: "New",
    collectionId: "signatures",
    createdAt: "2025-08-08",
    imgIdx: 1,
  }),
  make(23, {
    name: "Velours Rouge",
    family: "Amber",
    gender: "Feminine",
    concentration: "Parfum",
    notes: "Rose, Amber, Praline",
    keyNotes: ["Rose", "Amber", "Praline"],
    price: 285,
    collectionId: "nocturnes",
    createdAt: "2024-12-12",
    imgIdx: 1,
  }),
  make(24, {
    name: "Cèdre Fumé",
    family: "Woody",
    gender: "Masculine",
    concentration: "EDP",
    notes: "Cedar, Smoke, Vetiver",
    keyNotes: ["Cedar", "Smoke", "Vetiver"],
    price: 245,
    collectionId: "voyages",
    createdAt: "2025-01-05",
    imgIdx: 2,
  }),
];

export const FAMILIES: FragranceFamily[] = [
  "Floral",
  "Oriental",
  "Woody",
  "Leather",
  "Chypre",
  "Amber",
  "Green",
];
export const GENDERS: Gender[] = ["Feminine", "Masculine", "Unisex"];
export const CONCENTRATIONS: Concentration[] = ["EDT", "EDP", "Parfum", "Extrait"];

export const SORT_OPTIONS = [
  { value: "featured", label: "Featured" },
  { value: "newest", label: "Newest" },
  { value: "price-asc", label: "Price: Low to High" },
  { value: "price-desc", label: "Price: High to Low" },
  { value: "name-asc", label: "Name: A–Z" },
] as const;

export type SortValue = (typeof SORT_OPTIONS)[number]["value"];

export type CatalogFilters = {
  q: string;
  families: string[];
  genders: string[];
  concentrations: string[];
  collection: string;
  min: number;
  max: number;
  sort: SortValue;
  /** Optional: scope to a single CategoryRegistry id (Prompt 16). */
  categoryId?: string;
  /** Optional: generic enum attribute filters, keyed by facet key. */
  attrs?: Record<string, string[]>;
  /** Optional: generic numeric range attribute filters. */
  ranges?: Record<string, { min?: number; max?: number }>;
  /** Optional: generic boolean attribute filters. */
  bools?: Record<string, boolean>;
};

export function filterAndSort(products: CatalogProduct[], f: CatalogFilters): CatalogProduct[] {
  const q = f.q.trim().toLowerCase();
  let out = products.filter((p) => {
    if (q) {
      const attrHay = p.attributes
        ? Object.values(p.attributes)
            .map((v) => (Array.isArray(v) ? v.join(" ") : String(v ?? "")))
            .join(" ")
        : "";
      const hay = `${p.name} ${p.family} ${p.notes} ${p.gender} ${attrHay}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (f.categoryId && (p.categoryId ?? "personal-perfumes") !== f.categoryId) return false;
    if (f.families.length && !f.families.includes(p.family)) return false;
    if (f.genders.length && !f.genders.includes(p.gender)) return false;
    if (f.concentrations.length && !f.concentrations.includes(p.concentration)) return false;
    if (f.collection && p.collectionId !== f.collection) return false;
    if (p.price < f.min || p.price > f.max) return false;
    if (f.attrs) {
      for (const [key, values] of Object.entries(f.attrs)) {
        if (!values || values.length === 0) continue;
        const av = p.attributes?.[key];
        const asArr = Array.isArray(av) ? av.map(String) : av != null ? [String(av)] : [];
        if (!values.some((v) => asArr.includes(v))) return false;
      }
    }
    if (f.ranges) {
      for (const [key, r] of Object.entries(f.ranges)) {
        const raw = p.attributes?.[key];
        const num = typeof raw === "number" ? raw : Number(raw);
        if (!Number.isFinite(num)) return false;
        if (r.min != null && num < r.min) return false;
        if (r.max != null && num > r.max) return false;
      }
    }
    if (f.bools) {
      for (const [key, want] of Object.entries(f.bools)) {
        const raw = p.attributes?.[key];
        if (Boolean(raw) !== want) return false;
      }
    }
    return true;
  });

  switch (f.sort) {
    case "newest":
      out = out.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
      break;
    case "price-asc":
      out = out.sort((a, b) => a.price - b.price);
      break;
    case "price-desc":
      out = out.sort((a, b) => b.price - a.price);
      break;
    case "name-asc":
      out = out.sort((a, b) => a.name.localeCompare(b.name));
      break;
    case "featured":
    default:
      out = out.sort((a, b) => {
        const rank = (p: CatalogProduct) =>
          p.badge === "Bestseller" ? 0 : p.badge === "Limited" ? 1 : p.badge === "New" ? 2 : 3;
        return rank(a) - rank(b);
      });
  }
  return out;
}

// Catalog prices are INR. The bounds must span the whole live catalogue
// (entry-level 10 ML decants through limited-edition 100 ML flacons), otherwise
// the default price window silently hides every product.
export const PRICE_MIN = 0;
export const PRICE_MAX = 25000;
export const PAGE_SIZE = 8;
